import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { StyledHeader, StyledSidebar } from './styles';
import { KizenTypography } from '__app/typography';
import {
  type Folder,
  changeDirectoryAction,
  selectAllNonRootFolders,
  selectCurrentDirectory,
  selectDirectoryLocation,
  selectRootFolder,
} from 'store/automationsPage/automations.redux';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FoldersTree, type FolderStruct } from '@kizen/kds/FoldersTree';
import { Icon } from '@kizen/kds/Icon';
import { getFolderForFolderTree } from './helpers';
import { type ActionOption, RenderFolderMenu } from './RenderFolderMenu';
import { FoldersWrapper } from './FoldersWrapper';

export const AutomationFolders: FC<{
  scrolledToTable: boolean;
  onSelectAction: (
    action: ActionOption,
    folder: Folder,
    parentFolder?: Folder
  ) => void;
  canEdit: boolean;
  canDelete: boolean;
}> = ({ scrolledToTable, onSelectAction, canEdit, canDelete }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const rootFolder = useSelector(selectRootFolder);
  const nonRootFolders = useSelector(selectAllNonRootFolders);
  const currentDirectory = useSelector(selectCurrentDirectory);
  const currentLocation = useSelector(selectDirectoryLocation);

  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(
    new Set(rootFolder?.id ? [rootFolder.id] : [])
  );

  useEffect(() => {
    if (currentLocation) {
      setExpandedFolderIds(
        (prev) =>
          new Set([
            ...prev,
            ...currentLocation.toSpliced(-1, 1).map((folder) => folder.id),
          ])
      );
    }
  }, [currentLocation]);

  const rootFolderDTO = useMemo(() => {
    return getFolderForFolderTree(rootFolder, t);
  }, [rootFolder, t]);

  const nonRootFoldersDTO = useMemo(() => {
    return nonRootFolders.map((folder) => getFolderForFolderTree(folder, t));
  }, [nonRootFolders, t]);

  const handleChangeSelected = (folder: FolderStruct<Folder>) => {
    dispatch(changeDirectoryAction({ folder: folder.data, page: 1 }));
  };

  const handleChangeExpanded = (nextExpandedFolderIds: Set<string>) => {
    setExpandedFolderIds(nextExpandedFolderIds);
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <StyledSidebar>
      <StyledHeader scrolled={scrolledToTable}>
        <Icon icon="file-sidebar" size="base" />
        <KizenTypography weight="bold">{t('Folders')}</KizenTypography>
      </StyledHeader>
      <FoldersWrapper scrollContainerRef={scrollContainerRef}>
        <FoldersTree
          rootFolder={rootFolderDTO}
          folders={nonRootFoldersDTO}
          selectedFolderId={currentDirectory?.id ?? null}
          onChangeSelected={handleChangeSelected}
          expandedFolderIds={expandedFolderIds}
          onChangeExpanded={handleChangeExpanded}
          renderTrailing={(props) =>
            canEdit ? (
              <RenderFolderMenu
                {...props}
                onSelectAction={onSelectAction}
                scrollContainerRef={scrollContainerRef}
                canDelete={canDelete}
              />
            ) : null
          }
        />
      </FoldersWrapper>
    </StyledSidebar>
  );
};
